import type {
  Page,
  ReportFigure,
  ReportFilterOption,
  ReportOptionFilterKey,
  ReportQuery,
  ReportRecordsQuery,
  ReportSummary,
  ReportTrend,
} from '@chukua/common';
import { toListParams } from '../../shared/policies/list-params';
import { api, apiDownload } from '../../shared/shared.api';

export type { ReportFigure, ReportFilterOption, ReportSummary, ReportTrend };

/** One row behind a figure: the values of the report's `columns`, by key. */
export type ReportRecord = Record<string, string | number | null>;

export function listReports(): Promise<ReportSummary[]> {
  return api<ReportSummary[]>('/reports');
}

export function getFigures(key: string, query: ReportQuery): Promise<Page<ReportFigure>> {
  return api<Page<ReportFigure>>(`/reports/${key}?${toListParams(query)}`);
}

export function getRecords(key: string, query: ReportRecordsQuery): Promise<Page<ReportRecord>> {
  return api<Page<ReportRecord>>(`/reports/${key}/records?${toListParams(query)}`);
}

/** The trend takes the figures' filters and ignores the paging. */
export function getTrend(key: string, query: ReportQuery): Promise<ReportTrend> {
  return api<ReportTrend>(`/reports/${key}/trend?${toListParams(query)}`);
}

export function searchFilterOptions(filter: ReportOptionFilterKey, q: string | undefined): Promise<ReportFilterOption[]> {
  return api<ReportFilterOption[]>(`/reports/options/${filter}${q ? `?${new URLSearchParams({ q })}` : ''}`);
}

/** The labels for values already in the URL, so a picker opened on a drill-through shows names. */
export function labelFilterOptions(filter: ReportOptionFilterKey, values: string[]): Promise<ReportFilterOption[]> {
  const params = new URLSearchParams(values.map((value) => ['value', value]));
  return api<ReportFilterOption[]>(`/reports/options/${filter}?${params}`);
}

export interface ReportFile {
  blob: Blob;
  rowCount: number;
  filename: string;
}

/** The screen's filters, search and sort, but not its page: the file holds every matching row. */
export async function exportReport(key: string, kind: 'figures' | 'records', query: ReportQuery | ReportRecordsQuery): Promise<ReportFile> {
  const { page: _page, pageSize: _pageSize, ...rest } = query;
  const { blob, headers } = await apiDownload(`/reports/${key}/export?${toListParams({ ...rest, kind })}`);
  const filename = /filename="([^"]+)"/.exec(headers.get('content-disposition') ?? '')?.[1] ?? `${key}-${kind}.xlsx`;
  return { blob, rowCount: Number(headers.get('x-report-row-count') ?? 0), filename };
}
